import { CloseCircleOutlined, EllipsisOutlined } from '@ant-design/icons';
import { Dropdown, MenuProps, Table, Tag, TagProps, Tooltip } from 'antd';
import { differenceInCalendarDays, formatISO } from 'date-fns';
import { PatientAssessmentLink } from 'lib/documents/patient-assessment/patient-assessment-link';
import { useAssessmentLinkAPI } from 'lib/hooks/tanstack/use-assessmentLink';
import { resolveError } from 'lib/utils/formatters/error/resolve-error';
import { useErrorListContext } from 'lib/utils/useErrorList';
import { Moment } from 'moment';
import { useMemo } from 'react';
import { theme } from 'styles/colors';
import { usePatientInfoContext } from '../../../../patient/_utils/patient-info/hook-context-patientinfo';

const STATUS_TO_TAG_MAP: Record<
  PatientAssessmentLink['status'],
  { tooltip: string; title: string; color?: TagProps['color'] }
> = {
  activated: {
    title: 'Activated',
    tooltip:
      'The Test-Taker has used this link to start a ConcussionRx Assessment.',
    color: theme.primary
  },
  expired: {
    title: 'Expired',
    tooltip: 'The link is expired.',
    // TODO: Darker text
    // TODO: background "pastel red"
    // color: theme.legacy.red.light,
    color: 'red'
  },
  pending: {
    title: 'Pending',
    tooltip: 'The test-taker has not used this link.',
    // color: theme.gauge.yellow
    color: 'processing'
  },
  deactivated: {
    title: 'Deactivated',
    tooltip: 'The test-taker has not used this link.',
    // TODO: Darker
    // color: theme.primary
    color: 'default'
  }
};

export function DisplayAssessmentLinksTable(props: {
  assessmentLinks: PatientAssessmentLink[];
}) {
  const errorList = useErrorListContext();
  const mutationActions = useAssessmentLinkAPI();
  const patientInfo = usePatientInfoContext();
  const dataSource = useMemo(
    () =>
      props.assessmentLinks.map(_ => ({
        ..._,
        ..._.metadata
      })),
    [props.assessmentLinks]
  );

  return (
    <Table
      dataSource={dataSource}
      columns={[
        {
          dataIndex: 'created',
          title: 'Created at',
          render(value: Moment) {
            return formatISO(value.toDate(), {
              representation: 'date'
            });
          }
        },
        {
          dataIndex: 'expiresAt',
          title: 'Expires at',
          render(value: Moment) {
            const isoDate = formatISO(value.toDate(), {
              representation: 'date'
            });

            const fromNow = differenceInCalendarDays(
              value.toDate(),
              Date.now()
            );
            if (fromNow === 0) {
              return 'Today';
            }
            if (fromNow < 0) {
              return `${isoDate}`;
            }

            return <Tooltip title={`In ${fromNow} days`}>{isoDate}</Tooltip>;
          }
        },
        {
          dataIndex: 'status',
          title: 'Status',
          render(value: PatientAssessmentLink['status']) {
            const val = STATUS_TO_TAG_MAP[value];
            return (
              <Tooltip title={val.tooltip}>
                <Tag color={val.color}>{val.title}</Tag>
              </Tooltip>
            );
          }
        },
        {
          title: '',
          render(_value: unknown, record: PatientAssessmentLink) {
            const menuItems: MenuProps['items'] = [
              {
                key: 'deactivate',
                label: 'Deactivate',
                icon: <CloseCircleOutlined />,
                disabled: record.status !== 'pending',
                onClick: () => {
                  errorList.errorList.removeError('deactivation', {
                    ignoreMissing: true
                  });
                  mutationActions.deactivate.mutate(
                    {
                      assessmentLinkId: record.id,
                      patientId: patientInfo.patientInfo.patient.id
                    },
                    {
                      onError(e) {
                        const error = resolveError(e);
                        errorList.errorList.addError(error, 'deactivation');
                      }
                    }
                  );
                }
              }
            ];

            return (
              <div>
                <Dropdown menu={{ items: menuItems }} trigger={['click']} arrow>
                  <Tooltip title={'Extra options'}>
                    {/* <BaseButton> */}
                    <EllipsisOutlined />
                    {/* </BaseButton> */}
                  </Tooltip>
                </Dropdown>
              </div>
            );
          }
        }
      ]}
      rowKey={'id'}
      pagination={{
        pageSize: 3
      }}
    />
  );
}
